import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  AlertTitle
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ArrowBack,
  Save,
  Cancel,
  RestartAlt,
  Numbers,
  Warning,
  CheckCircle
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useApiOperation } from '../hooks/useApiOperation';
import { documentNumberSequenceService } from '../services/configurationService';
import {
  DocumentNumberSequence,
  DocumentNumberSequenceCreate,
  DocumentNumberSequenceUpdate
} from '../types/configuration';

const documentTypes = [
  { value: 'estimate', label: 'Estimate' },
  { value: 'sales_order', label: 'Sales Order' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'delivery_challan', label: 'Delivery Challan' },
  { value: 'sales_return', label: 'Sales Return' },
  { value: 'purchase_order', label: 'Purchase Order' },
  { value: 'purchase_bill', label: 'Purchase Bill' },
  { value: 'purchase_return', label: 'Purchase Return' },
  { value: 'payment', label: 'Payment' },
  { value: 'credit_note', label: 'Credit Note' },
  { value: 'debit_note', label: 'Debit Note' }
];

const monthNames = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const DocumentSequenceConfigurationPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [sequences, setSequences] = useState<DocumentNumberSequence[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSequence, setEditingSequence] = useState<DocumentNumberSequence | null>(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [sequenceToReset, setSequenceToReset] = useState<DocumentNumberSequence | null>(null);

  const { execute: saveSequence, loading: saving } = useApiOperation(
    async (data: DocumentNumberSequenceCreate | DocumentNumberSequenceUpdate) => {
      if (editingSequence) {
        return await documentNumberSequenceService.update(editingSequence.id, data as DocumentNumberSequenceUpdate);
      } else {
        return await documentNumberSequenceService.create(data as DocumentNumberSequenceCreate);
      }
    }
  );

  const { execute: deleteSequence } = useApiOperation(
    async (id: string) => {
      await documentNumberSequenceService.delete(id);
    }
  );

  const { execute: resetSequence } = useApiOperation(
    async (id: string) => {
      await documentNumberSequenceService.resetSequence(id);
    }
  );

  const [formData, setFormData] = useState<DocumentNumberSequenceCreate>({
    document_type: 'invoice',
    prefix: 'INV',
    current_number: 1,
    padding_length: 5,
    separator: '-',
    reset_on_financial_year: false,
    financial_year_start_month: 4,
    is_active: true
  });

  useEffect(() => {
    loadSequences();
  }, []);

  const loadSequences = async () => {
    try {
      setLoading(true);
      const response = await documentNumberSequenceService.getList();
      setSequences(response.items);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to load document sequences', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (sequence?: DocumentNumberSequence) => {
    if (sequence) {
      setEditingSequence(sequence);
      setFormData({
        document_type: sequence.document_type,
        prefix: sequence.prefix,
        current_number: sequence.current_number,
        padding_length: sequence.padding_length,
        separator: sequence.separator,
        reset_on_financial_year: sequence.reset_on_financial_year,
        financial_year_start_month: sequence.financial_year_start_month,
        is_active: sequence.is_active
      });
    } else {
      setEditingSequence(null);
      setFormData({
        document_type: 'invoice',
        prefix: 'INV',
        current_number: 1,
        padding_length: 5,
        separator: '-',
        reset_on_financial_year: false,
        financial_year_start_month: 4,
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSequence(null);
  };

  const handleSave = async () => {
    try {
      await saveSequence(formData);
      enqueueSnackbar(
        editingSequence ? 'Sequence updated successfully' : 'Sequence created successfully',
        { variant: 'success' }
      );
      handleCloseDialog();
      loadSequences();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to save sequence', { variant: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this document sequence? This action cannot be undone.')) return;

    try {
      await deleteSequence(id);
      enqueueSnackbar('Sequence deleted successfully', { variant: 'success' });
      loadSequences();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to delete sequence', { variant: 'error' });
    }
  };

  const handleOpenResetDialog = (sequence: DocumentNumberSequence) => {
    setSequenceToReset(sequence);
    setResetDialogOpen(true);
  };

  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
    setSequenceToReset(null);
  };

  const handleReset = async () => {
    if (!sequenceToReset) return;

    try {
      await resetSequence(sequenceToReset.id);
      enqueueSnackbar('Sequence reset successfully', { variant: 'success' });
      handleCloseResetDialog();
      loadSequences();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to reset sequence', { variant: 'error' });
    }
  };

  const handleInputChange = (field: keyof DocumentNumberSequenceCreate, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generatePreviewNumber = () => {
    const paddedNumber = (formData.current_number || 1).toString().padStart(formData.padding_length || 5, '0');
    return `${formData.prefix}${formData.separator}${paddedNumber}`;
  };

  const getUsedDocumentTypes = () => {
    return sequences.map(seq => seq.document_type);
  };

  const getAvailableDocumentTypes = () => {
    const usedTypes = getUsedDocumentTypes();
    return documentTypes.filter(type => 
      !usedTypes.includes(type.value) || (editingSequence && editingSequence.document_type === type.value)
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/configuration')}
            variant="outlined"
            size="small"
          >
            Back to Configuration
          </Button>
        </Stack>
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Numbers fontSize="large" />
            <Box>
              <Typography variant="h4" component="h1">
                Document Number Sequences
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configure automatic numbering for different document types with custom prefixes
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            disabled={getAvailableDocumentTypes().length === 0}
          >
            Add Sequence
          </Button>
        </Box>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Important Information</AlertTitle>
        <Typography variant="body2">
          • Document numbers are generated automatically when creating new documents
        </Typography>
        <Typography variant="body2">
          • Resetting a sequence will start numbering from 1 again (admin only)
        </Typography>
        <Typography variant="body2">
          • Deleted document numbers are never reused to maintain audit trail
        </Typography>
        <Typography variant="body2">
          • Financial year reset automatically resets the sequence at the start of your financial year
        </Typography>
      </Alert>

      {/* Sequences Grid */}
      <Grid container spacing={3}>
        {sequences.map((sequence) => {
          const previewNumber = `${sequence.prefix}${sequence.separator}${sequence.current_number.toString().padStart(sequence.padding_length, '0')}`;
          const nextNumber = `${sequence.prefix}${sequence.separator}${(sequence.current_number + 1).toString().padStart(sequence.padding_length, '0')}`;
          
          return (
            <Grid item xs={12} md={6} lg={4} key={sequence.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" component="h2">
                      {documentTypes.find(dt => dt.value === sequence.document_type)?.label}
                    </Typography>
                    <Chip
                      label={sequence.is_active ? 'Active' : 'Inactive'}
                      color={sequence.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Prefix:
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {sequence.prefix}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Current Number:
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {sequence.current_number}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Next Document:
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace" fontWeight="bold" color="primary.main">
                        {nextNumber}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Padding:
                      </Typography>
                      <Typography variant="body2">
                        {sequence.padding_length} digits
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Financial Year Reset:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {sequence.reset_on_financial_year ? (
                          <>
                            <CheckCircle color="success" fontSize="small" />
                            <Typography variant="body2">
                              {monthNames.find(m => m.value === sequence.financial_year_start_month)?.label}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Disabled
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Format Preview:
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace" sx={{ 
                        bgcolor: 'grey.100', 
                        px: 1, 
                        py: 0.5, 
                        borderRadius: 1 
                      }}>
                        {previewNumber}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Last Updated:
                      </Typography>
                      <Typography variant="body2">
                        {new Date(sequence.updated_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box>
                    <Tooltip title="Edit Sequence">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(sequence)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Sequence">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(sequence.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box>
                    <Tooltip title="Reset Sequence (Admin Only)">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenResetDialog(sequence)}
                        color="warning"
                      >
                        <RestartAlt />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {sequences.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No document sequences configured
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create document number sequences to automatically number your business documents
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Sequence
          </Button>
        </Paper>
      )}

      {/* Sequence Form Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSequence ? 'Edit Document Sequence' : 'Create Document Sequence'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={formData.document_type}
                  onChange={(e) => handleInputChange('document_type', e.target.value)}
                  label="Document Type"
                  disabled={!!editingSequence}
                >
                  {getAvailableDocumentTypes().map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prefix"
                value={formData.prefix}
                onChange={(e) => handleInputChange('prefix', e.target.value.toUpperCase())}
                required
                placeholder="INV"
                helperText="Letters and numbers only"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Separator"
                value={formData.separator}
                onChange={(e) => handleInputChange('separator', e.target.value)}
                required
                placeholder="-"
                helperText="Character between prefix and number"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Current Number"
                type="number"
                value={formData.current_number}
                onChange={(e) => handleInputChange('current_number', parseInt(e.target.value) || 1)}
                required
                inputProps={{ min: 1 }}
                helperText="Starting number for new documents"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Padding Length"
                type="number"
                value={formData.padding_length}
                onChange={(e) => handleInputChange('padding_length', parseInt(e.target.value) || 5)}
                required
                inputProps={{ min: 1, max: 10 }}
                helperText="Number of digits (with leading zeros)"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                bgcolor: 'grey.100', 
                p: 2, 
                borderRadius: 1,
                textAlign: 'center'
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Preview Format:
                </Typography>
                <Typography variant="h6" fontFamily="monospace" color="primary.main">
                  {generatePreviewNumber()}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.reset_on_financial_year}
                    onChange={(e) => handleInputChange('reset_on_financial_year', e.target.checked)}
                  />
                }
                label="Reset on Financial Year"
              />
            </Grid>

            {formData.reset_on_financial_year && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Financial Year Start Month</InputLabel>
                  <Select
                    value={formData.financial_year_start_month}
                    onChange={(e) => handleInputChange('financial_year_start_month', e.target.value)}
                    label="Financial Year Start Month"
                  >
                    {monthNames.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            disabled={saving || !formData.document_type || !formData.prefix}
          >
            {editingSequence ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={handleCloseResetDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            Reset Document Sequence
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Warning: This action cannot be undone</AlertTitle>
            You are about to reset the document sequence for{' '}
            <strong>
              {sequenceToReset && documentTypes.find(dt => dt.value === sequenceToReset.document_type)?.label}
            </strong>
            . This will:
          </Alert>
          
          <Box component="ul" sx={{ pl: 2, mt: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Reset the current number back to 1
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Start generating new document numbers from the beginning
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Not affect existing documents (they keep their current numbers)
            </Typography>
            <Typography component="li" variant="body2">
              Require administrator privileges to perform
            </Typography>
          </Box>

          {sequenceToReset && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Current: {sequenceToReset.prefix}{sequenceToReset.separator}{sequenceToReset.current_number.toString().padStart(sequenceToReset.padding_length, '0')}
              </Typography>
              <Typography variant="body2" color="primary.main" fontWeight="bold">
                After Reset: {sequenceToReset.prefix}{sequenceToReset.separator}{'1'.padStart(sequenceToReset.padding_length, '0')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleReset}
            variant="contained"
            color="warning"
            startIcon={<RestartAlt />}
          >
            Reset Sequence
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentSequenceConfigurationPage;